// Legal Resources & News — real content scraped from hallinans.co.uk
// Articles with `body` render internally; others link to the original post (externalUrl).

const POST = (slug) => `https://www.hallinans.co.uk/post/${slug}`;

export const CATEGORIES = [
  {
    slug: "offences",
    title: "Criminal Offences",
    icon: "Gavel",
    description:
      "Providing information on the most common legal offences and relevant sentencing guidelines.",
    image:
      "https://static.wixstatic.com/media/a11288_0db53e229cd647f9a1852d47d8371078~mv2.jpg/v1/fill/w_800,h_440,al_c,q_80,enc_avif,quality_auto/a11288_0db53e229cd647f9a1852d47d8371078~mv2.jpg",
  },
  {
    slug: "procedure",
    title: "Procedure",
    icon: "FileText",
    description:
      "Clarifying the complex criminal justice system to help you navigate difficult circumstances.",
    image:
      "https://static.wixstatic.com/media/a11288_8a80adba4e6840aba9b0822ee7d0be48~mv2.jpg/v1/fill/w_800,h_440,al_c,q_80,enc_avif,quality_auto/Procedure.jpg",
  },
  {
    slug: "commentary",
    title: "Commentary",
    icon: "MessageSquareText",
    description:
      "Commentary and observations on topical issues relevant to the criminal justice system and the news.",
    image:
      "https://static.wixstatic.com/media/a11288_391d2b91e444469ab0ad27ac74d60e37~mv2.jpg/v1/crop/x_0,y_0,w_604,h_255/fill/w_800,h_440,al_c,q_80,enc_avif,quality_auto/Comment.jpg",
  },
  {
    slug: "news",
    title: "Hallinans News",
    icon: "Newspaper",
    description:
      "All about us and our history, as well as recent team and news updates.",
    image:
      "https://static.wixstatic.com/media/a11288_fc3ef6afe9614454a5e77c6806011c06~mv2.jpg/v1/fill/w_800,h_440,al_c,q_80,enc_avif,quality_auto/sticker%20copy.jpg",
  },
];

export const ARTICLES = [
  // ---------------- OFFENCES ----------------
  {
    slug: "coercive-and-controlling-behaviour",
    category: "offences",
    title: "Controlling and Coercive Behaviour – An Overview",
    author: "Niamh Dennis",
    date: "29 Apr 2026",
    readTime: "8 min read",
    externalUrl: POST("coercive-and-controlling-behaviour"),
  },
  {
    slug: "misconduct-in-public-office",
    category: "offences",
    title: "Misconduct in Public Office",
    author: "Namita Pawa",
    date: "19 Feb 2026",
    readTime: "6 min read",
    externalUrl: POST("misconduct-in-public-office"),
  },
  {
    slug: "what-is-modern-day-slavery",
    category: "offences",
    title: "What is Modern Day Slavery?",
    author: "Megan Farquhar",
    date: "4 Mar 2025",
    readTime: "7 min read",
    externalUrl: POST("what-is-modern-day-slavery"),
  },
  {
    slug: "drinking-over-the-festive-season",
    category: "offences",
    title: "Drinking over the Festive Season",
    author: "Megan Farquhar",
    date: "3 Dec 2024",
    readTime: "5 min read",
    externalUrl: POST("drinking-over-the-festive-season"),
  },
  {
    slug: "murder",
    category: "offences",
    title: "What is the sentence for murder?",
    author: "Redmond Traynor",
    date: "18 Nov 2024",
    readTime: "5 min read",
    standfirst:
      "Murder carries a mandatory minimum sentence of life imprisonment and perhaps the greatest stigma of any crime. If you need advice on a murder charge, speak with one of our defence solicitors today.",
    body: [
      { t: "h2", x: "What is murder?" },
      { t: "p", x: "The classic definition of murder is the unlawful killing of another with the intention to kill or to do grievous bodily harm." },
      { t: "p", x: "\u201cKilling\u201d means that the defendant by his act or omission contributed significantly to the death. \u201cUnlawful\u201d means that the killing is not, for example, in lawful self-defence or as part of surgical treatment." },
      { t: "p", x: "It is not necessary that there should be any direct contact between the accused and his victim. What is required only is that the accused's act or omission was a \u201csubstantial cause\u201d of death. That may be proved even where some other event, like medical negligence, also contributed to the death." },
      { t: "h2", x: "\u201cYear and a Day Rule\u201d changes" },
      { t: "p", x: "Before the law was reformed in 1996, murder could only be committed if the death occurred within a year and a day of the act which caused death (the \u201cYear and a Day Rule\u201d). Now, if an act can be shown to be a substantial cause of death it can be prosecuted as murder however much time has elapsed between the act and the death. Nevertheless, the consent of the Attorney General must be sought where three years or more separate the accused's act and the victim's death." },
      { t: "h2", x: "Intention to kill vs intention to cause grievous bodily harm" },
      { t: "p", x: "A conviction for murder does not require proof of an intention to kill. It is enough that the accused merely intended to cause \u201cgrievous bodily harm\u201d. Surprisingly then, the necessary mental element for an offence of murder is the same as that for wounding contrary to section 18 of the Offences Against the Persons Act 1861." },
      { t: "p", x: "In 2006, the Law Commission recommended proposals to reform the law of murder by creating a three-tier system which would distinguish murder involving an intention to kill (first-degree murder) from murders involving an intention only to do serious injury (second degree murder). The 2006 proposals were not put in place but discussion of them has been recently revived in a 2024 review commissioned after the conviction of Valdo Calocane for the killing of three innocent people in Nottingham." },
      { t: "h2", x: "Defences for murder" },
      { t: "ul", x: [
        "Self defence – where the defendant genuinely feared for his/her personal safety and acted reasonably and proportionately to protect themselves",
        "Defence of another – as per self-defence but that the individual feared for the personal safety of another party",
        "Not present – such that they could not have committed the offence; identification will be challenged and often the use of an alibi defence",
        "No intention to kill or cause grievous bodily harm",
        "Accident – accepting presence and participation but the mental element does not exist",
      ]},
      { t: "h2", x: "Partial defences for murder" },
      { t: "p", x: "There are three partial defences which are only available upon a charge of murder and which, when successfully relied upon, result in a conviction for manslaughter: diminished responsibility, loss of control, and killing in pursuit of a suicide pact." },
      { t: "h2", x: "What is the sentence for murder?" },
      { t: "p", x: "The sentence for murder is mandatory. Offenders aged 21 or over must be sentenced to imprisonment for life. Except where a \u201cwhole life order\u201d is imposed, the sentencing court must specify a \u201cminimum term\u201d. A whole life order means that an offender must spend the rest of their life in prison. The legal framework for setting the minimum term is governed by sections 321 and 322 and Schedule 21 of the Sentencing Act 2020." },
      { t: "p", x: "The \u201cminimum term\u201d is the term which an offender must spend in prison before becoming eligible to apply for parole. Eligibility for release is determined by the Parole Board, and offenders who have been released will remain on licence for the rest of their life." },
      { t: "h2", x: "Framework for murder sentencing" },
      { t: "ul", x: [
        "A whole life order in cases of an \u201cexceptionally high\u201d level of seriousness — for example, murders involving sexual or sadistic conduct, the murder of a child involving abduction, or the murder of a police officer in the course of duty",
        "A starting point of 30 years in cases of a \u201cparticularly high\u201d level of seriousness — such as murder involving a firearm, murder aggravated by racial or religious hostility, or the murder of two or more persons",
        "A starting point of 25 years where the offender took a knife or other weapon to the scene intending to commit an offence and used it when committing the murder",
        "For any other case, the appropriate starting point may be 15 years",
      ]},
      { t: "h2", x: "Is a sentence reduced if you plead guilty to murder?" },
      { t: "p", x: "Credit for a guilty plea is available in cases of murder but the process is different. Where the court determines a whole life order, there is no reduction for a guilty plea. Where the court imposes a minimum term, the maximum reduction is one sixth of the minimum term or 5 years (whichever is less), and only where a guilty plea was indicated at the first stage of proceedings. A maximum of one-twentieth is available for a guilty plea on the day of trial." },
      { t: "p", x: "Guest written by Redmond Traynor from 2 Hare Court chambers." },
    ],
  },
  {
    slug: "bribery",
    category: "offences",
    title: "A Guide to The Bribery Act 2010",
    author: "Eleanor Fox",
    date: "19 Sep 2024",
    readTime: "7 min read",
    externalUrl: POST("bribery"),
  },
  {
    slug: "offensive-weapons",
    category: "offences",
    title: "Offensive Weapons",
    author: "Stuart McDonald",
    date: "3 Sep 2024",
    readTime: "2 min read",
    externalUrl: POST("offensive-weapons"),
  },
  {
    slug: "dangerous-dogs",
    category: "offences",
    title: "Dangerous Dogs",
    author: "Rai Gill",
    date: "28 Aug 2024",
    readTime: "4 min read",
    externalUrl: POST("dangerous-dogs"),
  },
  {
    slug: "controlling-and-coercive-behaviour",
    category: "offences",
    title: "Controlling and Coercive Behaviour",
    author: "Stuart McDonald",
    date: "13 Aug 2024",
    readTime: "3 min read",
    externalUrl: POST("controlling-and-coercive-behaviour"),
  },
  {
    slug: "domestic-violence-and-domestic-abuse",
    category: "offences",
    title: "Domestic Violence and Domestic Abuse",
    author: "Stuart McDonald",
    date: "13 Aug 2024",
    readTime: "2 min read",
    externalUrl: POST("domestic-violence-and-domestic-abuse"),
  },
  {
    slug: "money-laundering",
    category: "offences",
    title: "Money Laundering: Your Definitive Guide",
    author: "Namita Pawa",
    date: "23 Jul 2024",
    readTime: "6 min read",
    standfirst:
      "Money laundering is defined in the Proceeds of Crime Act 2002 as \u201cthe process by which the proceeds of crime are converted into assets which appear to have a legitimate origin, so that they can be retained permanently or recycled into further criminal enterprises\u201d.",
    body: [
      { t: "p", x: "You have all heard of the expression \u2018money laundering\u2019 and often the association can be gangsters having to \u2018wash\u2019 or \u2018clean\u2019 the fruits of their criminality so that it can recirculate and have the appearance of clean money." },
      { t: "p", x: "Money laundering can be broken down into two categories: those who commit offences and then launder the proceeds of those criminal offences; and those whose only criminal involvement is to launder the proceeds of crime committed by others." },
      { t: "p", x: "Interestingly, there is no actual \u2018money laundering\u2019 offence, but there are ways one can commit money laundering as set out in Part 7 of POCA, including:" },
      { t: "ul", x: [
        "Section 327: concealing, disguising, converting or transferring criminal property, or removing criminal property from the jurisdiction",
        "Section 328: entering into an arrangement concerning criminal property which facilitates the acquisition, retention, use or control of criminal property by or on behalf of another person",
        "Section 329: acquiring, using or having possession of criminal property",
      ]},
      { t: "h2", x: "What does the prosecution need to prove?" },
      { t: "p", x: "\u2018Criminal property\u2019 is a person's benefit from criminal conduct. \u2018Property\u2019 includes money, all forms of property or real estate, things in action and other intangible property. As to state of mind, the offender must know or suspect that the property represents the benefit from criminal conduct." },
      { t: "p", x: "When an individual is charged, the charge will either be a money laundering count on its own or on an indictment with the underlying offence that gives rise to the criminal property — such as a fraud or drugs offence. Where money laundering is charged alone, the prosecution either need to prove the type of offending that resulted in the criminal property, or rely on evidence giving rise to an \u2018irresistible inference that it could only have been derived from crime\u2019 (R v Anwoir [2008] EWCA Crim 1354)." },
      { t: "h2", x: "Defences" },
      { t: "ul", x: [
        "Lack of knowledge or suspicion: if a person can prove that they did not know or suspect, and had no reasonable grounds for knowing or suspecting, that the property was criminal property, this is a complete defence",
        "Authorised disclosure (section 338): making an authorised disclosure to the National Crime Agency by filing a Suspicious Activity Report and obtaining consent before proceeding with a transaction",
        "Compliance with due diligence: a business demonstrating it has complied with all due diligence requirements, including customer due diligence, ongoing monitoring and record-keeping",
        "Adequate consideration: if the property was obtained for adequate consideration without knowing or suspecting it to be the proceeds of crime",
      ]},
      { t: "p", x: "These defences are complex and require careful legal consideration. Each case needs to be considered on its own facts — anyone implicated in a money laundering investigation should seek legal advice to understand the full scope of their options." },
      { t: "h2", x: "Sentence" },
      { t: "p", x: "Money laundering offences are \u2018either way\u2019 offences, meaning they can be dealt with either in the magistrates' court or the Crown Court. In the magistrates' court, sentencing powers are limited to up to 6 months' imprisonment and/or a fine. In the Crown Court, offences under POCA carry maximum sentences of up to 14 years' imprisonment and unlimited fines." },
      { t: "p", x: "Following a conviction, in addition to sentence being passed, confiscation proceedings can follow to seize assets believed to be derived from, or intended for use in, unlawful conduct. These are complex proceedings for which further advice should be sought." },
      { t: "h2", x: "Key anti-money laundering regulations in the UK" },
      { t: "ul", x: [
        "Proceeds of Crime Act 2002 (POCA) — the foundational legislation defining money laundering offences and providing for confiscation, civil recovery and enforcement powers",
        "Terrorism Act 2000 — provisions for freezing assets and preventing funding of terrorist activities",
        "Money Laundering, Terrorist Financing and Transfer of Funds Regulations 2017 — detailed AML obligations for businesses including customer due diligence, record-keeping and reporting",
        "Money Laundering Regulations 2019 — an update reinforcing the legal framework for AML measures",
        "Economic Crime and Corporate Transparency Act 2023 — new measures including information sharing between businesses to prevent, detect and investigate economic crime",
      ]},
      { t: "p", x: "The UK's AML framework is robust and continuously evolving. Compliance is mandatory for businesses, especially those in the financial sector, and most people working in a regulated sector must receive regular AML training." },
    ],
  },
  {
    slug: "sexual-offences-and-consent",
    category: "offences",
    title: "Sexual Offences and Consent",
    author: "Stuart McDonald",
    date: "18 Jul 2024",
    readTime: "2 min read",
    externalUrl: POST("sexual-offences-and-consent"),
  },

  // ---------------- PROCEDURE ----------------
  {
    slug: "what-is-a-pre-sentence-report",
    category: "procedure",
    title: "What is a Pre-Sentence Report and what can I expect from my meeting with the Probation Service?",
    author: "Ben Baker",
    date: "28 Apr 2026",
    readTime: "5 min read",
    externalUrl: POST("what-is-a-pre-sentence-report-and-what-can-i-expect-from-my-meeting-with-the-probation-service"),
  },
  {
    slug: "sentencing-act-2026",
    category: "procedure",
    title: "Sentencing Act 2026: Everything You Need to Know About the New UK Sentencing Reforms",
    author: "Namita Pawa",
    date: "27 Jan 2026",
    readTime: "5 min read",
    externalUrl: POST("sentencing-act-2026-everything-you-need-to-know-about-the-new-uk-sentencing-reforms"),
  },
  {
    slug: "police-phone-pin",
    category: "procedure",
    title: "The Police have my phone. Do I have to give the Police my phone PIN, Passcode or Password?",
    author: "Megan Farquhar",
    date: "20 Jul 2025",
    readTime: "4 min read",
    externalUrl: POST("the-police-have-my-phone-do-i-have-to-give-the-police-my-phone-pin-passcode-or-password"),
  },
  {
    slug: "when-will-my-conviction-be-spent",
    category: "procedure",
    title: "How Long Does a Conviction Stay on My Record? When Will My Conviction Be Spent?",
    author: "Dominic Livesey",
    date: "8 Jul 2025",
    readTime: "4 min read",
    standfirst:
      "A common misconception is that a criminal conviction stays on your record forever. In reality, that's not always the case. Whether a conviction remains on your record — and for how long — depends on the nature of the offence, the sentence imposed, and the purpose of the background check.",
    body: [
      { t: "h2", x: "What is a conviction?" },
      { t: "p", x: "A person is \u201cconvicted\u201d when they either plead guilty or are found guilty at trial in the Magistrates' Court or Crown Court. From that point on, they are officially considered to have a criminal conviction. The details are recorded by the court and passed to the police to be entered into the national database." },
      { t: "h2", x: "What does \u201cspent\u201d mean?" },
      { t: "p", x: "Under the Rehabilitation of Offenders Act 1974, a \u201cspent\u201d conviction is one that you no longer have to legally disclose after a certain amount of time — known as the rehabilitation period. Once a conviction is spent, it typically does not need to be mentioned when applying for most jobs, housing, or insurance. Not all convictions are treated equally: some become spent immediately, others may never become spent." },
      { t: "h2", x: "When is a conviction never spent?" },
      { t: "p", x: "Some convictions can never be spent, regardless of how much time has passed. This usually applies when a sentence of life imprisonment or custody for life is imposed, or the conviction is for a \u201cspecified offence\u201d under Schedule 18 of the Sentencing Act 2020 — including murder, rape and serious sexual offences, kidnapping and false imprisonment, and terrorism-related offences. Such convictions will always appear on background checks and must always be disclosed." },
      { t: "h2", x: "When is a conviction spent immediately?" },
      { t: "p", x: "A conviction is spent immediately if the sentence was a simple caution, a youth caution, or an absolute discharge. These do not carry a rehabilitation period." },
      { t: "h2", x: "Rehabilitation periods for other sentences" },
      { t: "ul", x: [
        "Fines: spent after 12 months from the date of conviction",
        "Compensation orders: spent immediately after full payment is made",
        "Community orders or disqualifications: spent on the date set by the court, or 2 years after conviction if no date is set",
        "Custodial sentences over 4 years: spent 7 years after the sentence is completed",
        "Custodial sentences of 1 to 4 years: spent 4 years after completion",
        "Custodial sentences up to 1 year: spent 1 year after completion",
      ]},
      { t: "p", x: "Suspended sentences follow the same rules as custodial sentences, with the rehabilitation period starting after the end of the full term. Where sentences are concurrent, the longest determines the rehabilitation period; where consecutive, their lengths are added together. Different rules apply to individuals convicted under the age of 18." },
      { t: "h2", x: "Do I have to tell my employer about my conviction?" },
      { t: "p", x: "If asked, you must disclose any unspent convictions to an employer. Once a conviction is spent, you usually don't have to disclose it — unless you're applying for certain regulated roles, such as becoming a solicitor or doctor, which have separate disclosure requirements." },
      { t: "h2", x: "Will my conviction show up on a DBS check?" },
      { t: "p", x: "There are three types of Disclosure and Barring Service (DBS) checks: Basic, Standard and Enhanced. A spent conviction will not appear on a Basic DBS check, but may still appear on Standard or Enhanced checks depending on the nature of the offence." },
      { t: "h2", x: "What is filtering?" },
      { t: "p", x: "Filtering removes certain convictions and cautions from Standard and Enhanced DBS checks after a specific time period. For adults, cautions are filtered after 6 years and convictions after 11 years; for those under 18 at conviction, after 2 and 5.5 years respectively. Specified offences are never filtered. Once filtered, these become \u201cprotected\u201d convictions or cautions and you are not legally required to disclose them." },
      { t: "p", x: "A small number of roles (e.g. in national security, the police, judiciary, or firearms licensing) allow direct access to the Police National Computer and may still view spent and filtered convictions." },
    ],
  },
  {
    slug: "police-cash-seizure",
    category: "procedure",
    title: "Police Cash Seizure",
    author: "Ben Baker",
    date: "8 Jun 2025",
    readTime: "6 min read",
    externalUrl: POST("police-cash-seizure"),
  },
  {
    slug: "the-uk-supreme-court",
    category: "procedure",
    title: "The UK Supreme Court",
    author: "Ben Baker",
    date: "9 Apr 2025",
    readTime: "4 min read",
    externalUrl: POST("the-uk-supreme-court"),
  },
  {
    slug: "judicial-review",
    category: "procedure",
    title: "Judicial Review",
    author: "Rai Gill",
    date: "28 Jan 2025",
    readTime: "7 min read",
    externalUrl: POST("judicial-review"),
  },
  {
    slug: "changes-magistrates-court-sentencing",
    category: "procedure",
    title: "Changes to Sentencing in the Magistrates' Court",
    author: "Rai Gill",
    date: "29 Nov 2024",
    readTime: "4 min read",
    externalUrl: POST("changes-magistrates-court-sentencing"),
  },
  {
    slug: "trials-in-the-crown-court",
    category: "procedure",
    title: "Trials in the Crown Court",
    author: "Ben Baker",
    date: "30 Oct 2024",
    readTime: "11 min read",
    externalUrl: POST("trials-in-the-crown-court"),
  },
  {
    slug: "ptph-and-the-crown-court",
    category: "procedure",
    title: "What is a PTPH and what happens in the Crown Court?",
    author: "Namita Pawa",
    date: "4 Oct 2024",
    readTime: "6 min read",
    standfirst:
      "Our handy guide to Crown Court procedure. Every case, regardless of how serious, starts off in the magistrates' court — this article focuses on the cases that are sent to the Crown Court to be dealt with there.",
    body: [
      { t: "p", x: "There are some offences that can be dealt with in the Magistrates' Court or the Crown Court — known as \u2018either way offences\u2019. And there are some offences that can only be dealt with in the Crown Court — known as \u2018indictable only\u2019 offences. It can be quite confusing to know whether your case will need to be heard in the Magistrates' Court or the Crown Court, and whether you will have any say over where you will be tried." },
      { t: "h2", x: "First hearing in the Crown Court" },
      { t: "p", x: "Once your case leaves the Magistrates' Court, the first time you will appear in the Crown Court will be for a hearing called a Plea and Trial Preparation Hearing — referred to as a PTPH. This hearing usually takes place 28 days after your first hearing in the Magistrates' Court and, in any event, not later than 35 days after the date your case was sent." },
      { t: "p", x: "Before this hearing, it is important that you have met with your solicitor to discuss the initial evidence served in your case and to go through what to expect on the day. You will need to speak to your solicitor the previous working day to obtain the \u2018listing\u2019 — the time and court room your case will be heard in. Top tip: always allow extra time to get through security." },
      { t: "p", x: "Your barrister will try to secure a private and convenient place for a conference in which they will ensure you understand the nature of the charges and the evidence in your case. They will go over your instructions and confirm the plea you will be entering. When your case is called, the usher will show you into the court room, where you will generally be required to go into the dock. The court clerk will ask you to confirm your name and read out the charge(s), and you will enter your pleas of guilty or not guilty." },
      { t: "h2", x: "Entering a not guilty plea" },
      { t: "p", x: "If you enter a not guilty plea to all charges, the Judge will go through a form completed by the advocates in advance, identifying the representatives for each side, the key issues, and which witnesses are likely to give evidence at trial. The Judge will then make directions — known as stage dates — to ensure preparation of the case is completed on time:" },
      { t: "ul", x: [
        "Stage 1: the prosecution must serve all of the material in their case, including any unused material — ordinarily 50 days after the PTPH for those in custody and 70 days for those on bail",
        "Stage 2: the defence serve a defence statement setting out their defence, making disclosure requests and identifying which prosecution witnesses must give live evidence — generally 28 days after stage 1",
        "Stage 3: the prosecution must respond to the defence statement, addressing the disclosure requests — 14 or 28 days after stage 2 depending on the proximity of trial",
        "Stage 4: the prosecution or defence must make any applications or requests by this date",
      ]},
      { t: "p", x: "The Court will then set a date for both parties to serve a certificate of trial readiness, fix a pre-trial review hearing (PTR), and set the trial date with a time estimate. Cases that meet the criteria are given a trial fixture — a precise date; other cases are given a \u2018warned list\u2019 period in which it is hoped the case will be called on." },
      { t: "h2", x: "Entering a guilty plea" },
      { t: "p", x: "If you enter a guilty plea to all charges, the Court will need to determine whether it can proceed to sentence and whether there is a basis of plea — a document setting out the extent to which you accept the prosecution case. In some cases, the defence may invite the Court to adjourn sentence to a later date so they can prepare mitigation and a pre-sentence report (PSR) can be prepared by Probation." },
      { t: "p", x: "Other considerations include whether a Newton hearing (a trial of issue to determine the extent of your guilt) is needed, whether you have pleaded not guilty to other counts, or whether there are co-defendants standing trial — in which case it is common for sentence to be put back until after the linked trial." },
      { t: "h2", x: "Bail or remand?" },
      { t: "p", x: "Once pleas have been entered and the next dates confirmed, the Court will confirm your release on bail (reminding you of any conditions that apply) or remand you in custody. The Court may also hear a bail application if the necessary paperwork has been lodged." },
      { t: "h2", x: "After the PTPH" },
      { t: "p", x: "The next steps will be determined by the nature of your plea. Your barrister will discuss the hearing with you once it has concluded, and you will receive correspondence confirming what happened, the key dates and what will happen next. It is important to keep in touch with your solicitor as they will want to go through the evidence with you when it is served, or prepare your case for the sentence hearing." },
    ],
  },
  {
    slug: "criminal-or-prison-solicitor",
    category: "procedure",
    title: "What is the difference between a criminal lawyer and a prison lawyer?",
    author: "Megan Farquhar",
    date: "19 Sep 2024",
    readTime: "3 min read",
    externalUrl: POST("criminal-or-prison-solicitor"),
  },
  {
    slug: "prisons-in-england-and-wales",
    category: "procedure",
    title: "Prisons in England and Wales",
    author: "Namita Pawa",
    date: "3 Sep 2024",
    readTime: "4 min read",
    externalUrl: POST("prisons-in-england-and-wales"),
  },

  // ---------------- COMMENTARY ----------------
  {
    slug: "delay-in-the-criminal-justice-system",
    category: "commentary",
    title: "Delay in the Criminal Justice System: a Defence Perspective",
    author: "Connor Byrne",
    date: "28 Apr 2026",
    readTime: "6 min read",
    standfirst:
      "The impact of delay on victims of crime has been placed at the heart of the conversation around delay in the criminal justice system. However, if you are accused of a crime, the impact of delay in your case progressing and concluding can be just as serious.",
    body: [
      { t: "p", x: "It is important to be aware of the different ways in which delay can arise, and the ways that delay can be dealt with in the correct circumstances. This article considers delay from the defence perspective — from an allegation being made all the way through to sentence." },
      { t: "h2", x: "Before court" },
      { t: "p", x: "The first form of delay to note is a delay in an allegation being made, or an investigation commencing. Summary only offences generally have a time limit of 6 months from the date of the alleged offence or the matter of the complaint being known (s.127 Magistrates' Courts Act 1980). However, indictable offences have no such time limit — a police investigation may begin years or decades after the events concerned. In such cases, memories fade and documentary evidence may have been lost; these issues might affect the ability to put forward a defence case but can also affect whether there is sufficiently reliable evidence to charge." },
      { t: "h2", x: "Police bail" },
      { t: "p", x: "Following arrest and interview, if the police are not ready to either charge a suspect or determine that no further action will be taken, the suspect will be released on police bail or released under investigation. The initial bail period is usually 3 months. An extension to 6 months must be authorised by an Inspector, and to 9 months by a Superintendent. Any subsequent extensions must be authorised by a Magistrate every 3 months. There is no legal limit to the length of time a person may be on police bail, but the Court must be satisfied the investigation is being conducted diligently." },
      { t: "h2", x: "Release under investigation" },
      { t: "p", x: "A suspect may instead be \u2018released under investigation\u2019 — the police have not concluded their investigation, but there are no bail conditions and no requirement to report to the police station. There is no legal limit on this period, which can often be several months or even years. During this period, defence lawyers can continue to be in contact with the police to request updates and keep the person under investigation informed of developments." },
      { t: "h2", x: "At court: waiting for trial" },
      { t: "p", x: "Whilst the phrase \u2018summary justice should be speedy justice\u2019 is often used, Magistrates' Courts are not immune to delays and backlogs. Ministry of Justice data states the average wait at the Magistrates' Court, for a defendant who has pleaded not guilty to one or more summary offences, is now 217 days from first appearance to the end of the case. A recent report by Transform Justice, based on observations of over 2,300 hearings in London Magistrates' Courts, found 37% of hearings ended in a delay or adjournment." },
      { t: "p", x: "Delays in the Crown Court have dominated the headlines, and the seriousness of this issue cannot be underestimated. Ministry of Justice data states the average wait for those who enter not guilty pleas is 60 weeks — and it is very possible that those entering the system now face even greater delays. In our experience, it is not unusual for trials now to be listed into 2029 or 2030 in some London Crown Courts." },
      { t: "p", x: "For those in prison awaiting trial, a shorter delay is common as the Crown Court will prioritise their cases. The custody time limit is set at 182 days for most Crown Court cases. However, it is frequently the case that Crown Courts cannot list a trial within that limit. In R (Bernard) v The Crown Court Sitting at Snaresbrook [2025] EWHC 3055 (Admin), the High Court questioned such extensions becoming automatically acceptable, finding that backlog or resourcing issues would not always be a \u2018sufficient cause\u2019 and that \u2018rigorous scrutiny\u2019 should be applied." },
      { t: "h2", x: "Delay and sentencing" },
      { t: "p", x: "If a person is convicted following a trial, the delay they have faced can impact the sentence in two significant ways. Firstly, delay can be a mitigating factor where there has been an unreasonable delay in proceedings since apprehension, the delay is not the fault of the offender, and it has had a detrimental effect on them. Secondly, if a person awaits trial on bail with an electronically monitored curfew of 9 hours or more a day (a \u2018qualifying curfew\u2019), section 325 of the Sentencing Act 2020 requires that two days spent on qualifying curfew result in one day being deducted from a sentence of imprisonment." },
      { t: "h2", x: "Conclusions" },
      { t: "p", x: "It is important to recognise that the wheels of justice are turning slowly at the moment, and the impact for those accused of a crime and their families. Whether this is likely to improve, even with the proposed reforms, is uncertain. Further comment will follow considering Part 2 of the Leveson Report and whether the solutions proposed are likely to reduce delay. If you require advice in relation to anything raised in this article, please contact our team." },
    ],
  },
  {
    slug: "mistaken-releases-from-prison",
    category: "commentary",
    title: "Mistaken Releases from Prison",
    author: "Connor Byrne",
    date: "19 Feb 2026",
    readTime: "4 min read",
    externalUrl: POST("mistaken-releases-from-prison"),
  },
  {
    slug: "fbi-style-policing",
    category: "commentary",
    title: "Here Come the 'Men in Black': Overhauling the UK Policing Framework with a new FBI-Style Policing Team",
    author: "Ben Baker",
    date: "27 Jan 2026",
    readTime: "2 min read",
    externalUrl: POST("overhauling-the-uk-policing-framework-with-new-fbi-style-policing"),
  },
  {
    slug: "right-to-trial-by-jury",
    category: "commentary",
    title: "12 Heads Are Better Than One — The Case for Preserving the Right to Trial by Jury in England & Wales",
    author: "Ben Baker",
    date: "1 Dec 2025",
    readTime: "10 min read",
    externalUrl: POST("12-heads-are-better-than-one-the-case-for-preserving-the-right-to-trial-by-jury-in-england-wales"),
  },
  {
    slug: "assisted-dying-bill-and-coercive-control",
    category: "commentary",
    title: "Assisted Dying Bill And Coercive Control: Through a Criminal Practitioner's Lens",
    author: "Ben Baker",
    date: "13 Feb 2025",
    readTime: "9 min read",
    externalUrl: POST("assisted-dying-bill-and-coercive-control-through-a-criminal-practitioner-s-lens"),
  },
  {
    slug: "lucy-letby-murder-convictions",
    category: "commentary",
    title: "Lucy Letby Murder Convictions: A Summary of The Latest Findings",
    author: "Ben Baker",
    date: "6 Feb 2025",
    readTime: "3 min read",
    standfirst:
      "On 4 February 2025, a new report was released by an expert panel on the details of the Lucy Letby murder convictions. This article summarises the main findings within this recent report.",
    body: [
      { t: "p", x: "Within the criminal justice system, it is important that the rights of victims, the accused and witnesses involved in a case are protected. This is especially important when dealing with crimes of a serious nature, such as the murder of multiple babies." },
      { t: "h2", x: "Murder convictions in 2023" },
      { t: "p", x: "Lucy Letby was convicted in 2023 of 7 counts of murdering a baby, and following a re-trial on another count, she was convicted in 2024 of attempted murder of a further baby. Letby was sentenced to two Whole Life Orders for her crimes. Letby was a nurse specialising in the intensive care of infants at the time of the offending." },
      { t: "h2", x: "New expert panel report" },
      { t: "p", x: "Doubt has now been cast on the safety of those convictions, with a panel of independent experts opining that there is no evidence that the babies had been murdered by Letby. The report was produced by a panel of 14 international experts specialising in an array of disciplines, chaired by neonatologist Dr Shoo Lee." },
      { t: "p", x: "Amongst the criticisms, it is posited that the victims are more likely to have died as a result of a poorly run healthcare system at the Countess of Chester Hospital, with the panel declaring there was \u2018no medical evidence supporting malfeasance causing death or injury\u2019 in any of the cases examined. It was found that the ward lacked clinical staff of appropriate qualification, with existing staff being over-worked. Dr Shoo Lee stated that \u2018had this ward been in Canada, it would have been shut down\u2019." },
      { t: "p", x: "The panel's 34-page report identifies significant failures by the hospital, including the failure to accurately diagnose diseases, inability to conduct basic medical procedures and delays in administering treatment — including one \u2018preventable death\u2019 where doctors failed to respond to routine warnings of a baby's condition." },
      { t: "h2", x: "What does this mean for the convictions?" },
      { t: "p", x: "It is said that this report is capable of casting significant doubt over the safety of the convictions in Lucy Letby's case. Some have posited that these developments are also indicative of wider concerns regarding the management and culture of the National Health Service and Crown Prosecution Service. It has been further alleged that this fresh evidence may suggest a \u2018cover-up\u2019 amongst senior NHS staff, with Letby representing a scapegoat for wider institutional failings." },
      { t: "h2", x: "Urged consideration of Letby's conduct" },
      { t: "p", x: "Whilst there are significant concerns raised by this report, those unconvinced have urged that Letby's general conduct be borne in mind — including reference in the media to her \u2018deadpan\u2019 attitude throughout the initial trial proceedings, comments made to others about being \u2018excited\u2019 to experience her first newborn death, and the various drawings and journal entries found in her home referring to the death of children." },
    ],
  },
  {
    slug: "single-justice-procedure",
    category: "commentary",
    title: "Single Justice Procedure — or Single In-Justice Procedure?",
    author: "Ben Baker",
    date: "29 Nov 2024",
    readTime: "7 min read",
    externalUrl: POST("single-justice-procedure"),
  },
  {
    slug: "what-is-operation-early-dawn",
    category: "commentary",
    title: "What is Operation Early Dawn?",
    author: "Namita Pawa",
    date: "3 Sep 2024",
    readTime: "1 min read",
    externalUrl: POST("what-is-operation-early-dawn"),
  },
  {
    slug: "career-and-industry-advice-from-tess",
    category: "commentary",
    title: "Career and Industry — Advice from Tess",
    author: "Tess McCarthy",
    date: "26 Jan 2024",
    readTime: "3 min read",
    externalUrl: POST("career-and-industry-advice-from-tess"),
  },
  {
    slug: "should-drugs-be-legalised",
    category: "commentary",
    title: "Should drugs be legalised?",
    author: "Hallinans",
    date: "23 Jan 2024",
    readTime: "3 min read",
    externalUrl: POST("should-drugs-be-legalised"),
  },
  {
    slug: "lawyer-barrister-solicitor",
    category: "commentary",
    title: "What is the difference between a lawyer, barrister and solicitor?",
    author: "Rai Gill",
    date: "16 Nov 2023",
    readTime: "3 min read",
    externalUrl: POST("lawyer-barrister-solicitor"),
  },
  {
    slug: "i-am-a-victim-of-crime",
    category: "commentary",
    title: "I am a victim of crime, can you help me?",
    author: "Namita Pawa",
    date: "16 Nov 2023",
    readTime: "3 min read",
    externalUrl: POST("i-am-a-victim-of-crime-can-you-help-me"),
  },

  // ---------------- NEWS ----------------
  {
    slug: "recent-case-success-conditional-discharge",
    category: "news",
    title: "Recent Case Success — Conditional Discharge in the Crown Court for drug-related offences",
    author: "Ben Baker",
    date: "21 Nov 2025",
    readTime: "1 min read",
    externalUrl: POST("recent-case-success-conditional-discharge-in-the-crown-court-for-drug-related-offences"),
  },
  {
    slug: "times-best-law-firm-2026",
    category: "news",
    title: "Hallinans listed in The Times Best Law Firm 2026, Ranked in Chambers & Partners 2026 and Legal 500",
    author: "Hallinans",
    date: "13 Nov 2025",
    readTime: "1 min read",
    standfirst:
      "Hallinans has been listed in The Times Best Law Firms 2026. We are immensely proud of this achievement, being commended for Crime.",
    body: [
      { t: "p", x: "This announcement, published today in The Times, marks the third of three hallmarks of recognition for our firm, with us also being listed in the legal directories of Chambers and Partners and Legal 500 as a Leading Firm. Our Managing Director, Namita Pawa, has also been recognised in Chambers and Partners and has moved up to Band 4." },
      { t: "p", x: "We take great pride in our work with the focus on our client at all times. These listings are a recognition of the hard work put in by the whole team at Hallinans." },
      { t: "p", x: "Thank you to the editorial teams at The Times, Chambers and Partners and Legal 500. A great deal of thanks to our clients and colleagues who have provided independent feedback to the two legal directories and said some very humbling things about our team and practice." },
    ],
  },
  {
    slug: "20-lessons-from-20-years",
    category: "news",
    title: "20 Lessons from 20 Years in Practice as a Criminal Defence Lawyer",
    author: "Namita Pawa",
    date: "11 Nov 2025",
    readTime: "6 min read",
    externalUrl: POST("20-lessons-from-20-years-in-practice-as-a-criminal-defence-lawyer"),
  },
  {
    slug: "the-role-of-a-paralegal",
    category: "news",
    title: "The Role of a Paralegal",
    author: "Dominic Livesey",
    date: "8 Jul 2025",
    readTime: "5 min read",
    externalUrl: POST("the-role-of-a-paralegal"),
  },
  {
    slug: "meet-the-team-louise-scott",
    category: "news",
    title: "Meet the Team: Louise Scott",
    author: "Louise Scott",
    date: "17 Jun 2025",
    readTime: "2 min read",
    externalUrl: POST("meet-the-team-louise-scott"),
  },
  {
    slug: "meet-the-team-ben",
    category: "news",
    title: "Meet the Team: Ben",
    author: "Ben Baker",
    date: "1 Nov 2024",
    readTime: "1 min read",
    externalUrl: POST("meet-the-team-ben"),
  },
  {
    slug: "stuart-departure",
    category: "news",
    title: "Stuart McDonald's Departure: A Look Back at the Legal Profession",
    author: "Stuart McDonald",
    date: "31 Oct 2024",
    readTime: "3 min read",
    externalUrl: POST("stuart-departure"),
  },
  {
    slug: "press-release-anna-machkevitch",
    category: "news",
    title: "Press Release: Anna Machkevitch",
    author: "Namita Pawa",
    date: "16 Sep 2024",
    readTime: "2 min read",
    externalUrl: POST("press-release-anna-machkevitch"),
  },
  {
    slug: "celebrating-women-in-criminal-law",
    category: "news",
    title: "Celebrating Women in Criminal Law",
    author: "Hallinans",
    date: "4 Mar 2024",
    readTime: "2 min read",
    externalUrl: POST("celebrating-women-in-criminal-law"),
  },
  {
    slug: "meet-the-team-namita",
    category: "news",
    title: "Meet the Team: Namita",
    author: "Namita Pawa",
    date: "16 Nov 2023",
    readTime: "2 min read",
    externalUrl: POST("meet-the-team-namita"),
  },
  {
    slug: "day-in-the-life",
    category: "news",
    title: "A Day in the Life at Hallinans",
    author: "Hallinans",
    date: "15 Nov 2023",
    readTime: "3 min read",
    externalUrl: POST("day-in-the-life"),
  },
  {
    slug: "history-hallinans",
    category: "news",
    title: "The History of Hallinans",
    author: "Nick Gay",
    date: "8 Nov 2023",
    readTime: "3 min read",
    standfirst:
      "The roots of this practice were established in Cardiff in the 1950s by Sir Charles Hallinan. The firm has thrived for more than 70 years and will surely thrive for the foreseeable future.",
    body: [
      { t: "p", x: "The roots of this practice were established in Cardiff in the 1950s by Sir Charles Hallinan, the original name of the firm now being lost in the mists of time. However we know that in the 1960s John Blackburn Gittings, the son of a former Chief Constable of Staffordshire Police, joined as an Articled Clerk. John used to tell us that in those days a solicitor could still use the cane to discipline an errant clerk! In due course he qualified as a solicitor and in 1970 he moved to London to open a satellite office, where initially he serviced a coterie of clients based mainly in Hertfordshire and Essex." },
      { t: "p", x: "The London branch became established very quickly in the early 70s and trainee solicitors and managing clerks were recruited to handle the growing amount of work. The early work was mainly funded from private means, as legal aid was not expanded to include criminal proceedings until the late 60s and funded only a small percentage of all criminal proceedings until the late 1970s. There was no publicly funded representation in police stations until 1986." },
      { t: "p", x: "In 1974 a young and fresh-faced Colin Nott joined the practice as a trainee. With the expansion of the Legal Aid scheme in criminal proceedings throughout the 1970s and the inception of the court duty solicitor scheme, the firm grew exponentially — growing to more than a dozen staff by the beginning of the next decade, including half a dozen solicitor advocates. In 1984 Colin Nott became a partner to John Blackburn Gittings in the practice." },
      { t: "p", x: "Following the implementation of the Police & Criminal Evidence Act and the consequent expansion of work through the police station advice and assistance scheme in 1986, the client base and number of cases grew. As the legal aid budget started to come under pressure, rates and fees were first effectively frozen and then subject to sustained cuts over the next 30 years. John Gittings left the practice to become the Attorney General of Gibraltar (and later a High Court Judge in southern Africa) and throughout the 90s Colin Nott ran the firm as a sole practitioner." },
      { t: "p", x: "Stuart McDonald, an experienced solicitor advocate, joined the firm in 2002 and held true the values at our core. Sian Williams, another experienced solicitor advocate, joined Colin in partnership, and when she left the profession in 2013 she was succeeded by Namita Pawa. Namita joined having gained experience in well-known London criminal law firms. She was invited to join Colin as an equity partner in 2016 before taking over the firm in 2022 when Colin retired." },
      { t: "p", x: "The writer recalls that when he joined the firm in 1976, legal aid was available without a means test for offences such as being drunk and disorderly. Today no-one can qualify for legal aid in the Magistrates' Court unless their matter passes a strict \u2018interests of justice test\u2019 and they earn less than £12,540 a year. The poor level of funding that has existed for more than 30 years has slowly driven away able and experienced practitioners. Thankfully there has been a recent recognition of this and a slight improvement in funding." },
      { t: "p", x: "The writer has witnessed many changes in the firm during the last 46 years, however some things don't change; people always need help and there will always be those who are willing and able to provide it. The firm has thrived for more than 70 years and will surely thrive for the foreseeable future." },
    ],
  },
];

export const getCategory = (slug) => CATEGORIES.find((c) => c.slug === slug);
export const getArticlesByCategory = (slug) =>
  ARTICLES.filter((a) => a.category === slug);
export const getArticle = (category, slug) =>
  ARTICLES.find((a) => a.category === category && a.slug === slug && a.body);
export const LATEST = ARTICLES.filter((a) => a.body).slice(0, 3);

// Map article authors to team profile slugs
export const AUTHOR_SLUGS = {
  "Namita Pawa": "namita-pawa",
  "Ben Baker": "ben-baker",
  "Connor Byrne": "connor-byrne",
  "Niamh Dennis": "niamh-dennis",
  "Louise Scott": "louise-scott",
};
